export type ReceivingQueueLine = {
  purchase_line_id: string;
  line_no: number;
  sku_code: string | null;
  sku_name: string | null;
  item_id: string | null;
  packaging_id: string | null;
  packaging_label: string | null;
  purchase_qty: number | null;
  purchase_unit_name: string | null;
  stock_units_per_purchase_unit: number | null;
  ordered_stock_qty: number;
  stock_unit_id: string | null;
  stock_unit_name: string | null;
  received_stock_qty: number;
  closed_stock_qty: number;
  remaining_stock_qty: number;
  receiving_ready: boolean;
  line_receiving_status: string;
  last_event_at: string | null;
};

export type ReceivingEventLine = {
  purchase_line_id: string;
  line_no: number;
  sku_code: string | null;
  sku_name: string | null;
  quantity_stock: number;
  stock_unit_name: string | null;
  movement_id: string | null;
};

export type ReceivingEvent = {
  event_id: string;
  event_status: "POSTED" | "VOIDED";
  occurred_at: string;
  actor_code: string | null;
  node_code: string | null;
  note: string | null;
  movement_batch_id: string;
  void_event_id: string | null;
  voided_at: string | null;
  void_reason_code: string | null;
  void_reason_label: string | null;
  void_note: string | null;
  void_actor_code: string | null;
  void_movement_batch_id: string | null;
  lines: ReceivingEventLine[];
};

export type ReceivingQueueReceipt = {
  receipt_id: string;
  branch_code: string;
  purchase_date: string;
  supplier_name: string | null;
  internal_bill_no: string | null;
  document_ref: string | null;
  receipt_created_at: string;
  last_event_at: string | null;
  line_count: number;
  ready_line_count: number;
  ordered_stock_qty: number;
  received_stock_qty: number;
  closed_stock_qty: number;
  remaining_stock_qty: number;
  receiving_status: "PENDING" | "PARTIAL" | "RECEIVED" | "CLOSED_WITH_SHORTAGE" | "NEEDS_SETUP";
  lines: ReceivingQueueLine[];
  receiving_events?: ReceivingEvent[];
};

export type ReceivingQueue = {
  contract: "GET_STOCK_PURCHASE_RECEIVING_QUEUE_V1";
  generated_at: string;
  organization_id: string;
  branch_id: string;
  cutover_effective_from: string;
  capabilities: { can_receive: boolean; can_close_remainder: boolean; can_void_receive?: boolean };
  summary: {
    filtered_receipt_count: number;
    pending_receipt_count: number;
    partial_receipt_count: number;
    needs_setup_receipt_count: number;
    received_receipt_count: number;
    closed_with_shortage_receipt_count: number;
  };
  pagination: { limit: number; offset: number; has_more: boolean };
  receipts: ReceivingQueueReceipt[];
};

export type ReceivingCloseReason = {
  reason_code: string;
  label_th: string;
  description_th: string | null;
  requires_note: boolean;
};

export type ReceivingVoidReason = ReceivingCloseReason;

export type ReceivingMutationResult = {
  ok: boolean;
  contract: string;
  result_status: string;
  event_id: string;
  voided_event_id?: string | null;
  movement_batch_id?: string | null;
  reason_code?: string | null;
  receipt?: Record<string, unknown>;
};

type GatewayEnvelope<T> = { ok: boolean; data?: T; error?: string };

type ClientOptions = { baseUrl: string; timeoutMs?: number };

type SessionInput = { deviceSecret: string; sessionToken: string };

export class PosReceivingGatewayClient {
  private readonly baseUrl: string;
  private readonly timeoutMs: number;

  constructor(options: ClientOptions) {
    this.baseUrl = options.baseUrl.replace(/\/+$/, "");
    this.timeoutMs = options.timeoutMs ?? 12_000;
  }

  queue(input: SessionInput & {
    searchText?: string;
    status?: string;
    includeResolved?: boolean;
    limit?: number;
    offset?: number;
  }) {
    return this.post<ReceivingQueue>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "QUEUE",
      search_text: input.searchText ?? null,
      status: input.status ?? null,
      include_resolved: input.includeResolved ?? false,
      limit: input.limit ?? 50,
      offset: input.offset ?? 0,
    });
  }

  closeReasons(input: SessionInput) {
    return this.post<{ contract: string; reasons: ReceivingCloseReason[] }>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "CLOSE_REASONS",
    });
  }

  voidReasons(input: SessionInput) {
    return this.post<{ contract: string; reasons: ReceivingVoidReason[] }>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "VOID_REASONS",
    });
  }

  confirm(input: SessionInput & {
    receiptId: string;
    lines: Array<{ purchase_line_id: string; accepted_stock_qty: number }>;
    requestKey: string;
    note?: string | null;
  }) {
    return this.post<ReceivingMutationResult>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "CONFIRM",
      receipt_id: input.receiptId,
      lines: input.lines,
      request_key: input.requestKey,
      note: input.note ?? null,
    });
  }

  closeRemainder(input: SessionInput & {
    receiptId: string;
    lines: Array<{ purchase_line_id: string; close_stock_qty: number }>;
    reasonCode: string;
    requestKey: string;
    note?: string | null;
  }) {
    return this.post<ReceivingMutationResult>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "CLOSE_REMAINDER",
      receipt_id: input.receiptId,
      lines: input.lines,
      reason_code: input.reasonCode,
      request_key: input.requestKey,
      note: input.note ?? null,
    });
  }

  voidReceive(input: SessionInput & {
    receivingEventId: string;
    reasonCode: string;
    requestKey: string;
    note?: string | null;
  }) {
    return this.post<ReceivingMutationResult>({
      device_secret: input.deviceSecret,
      session_token: input.sessionToken,
      action: "VOID_RECEIVE",
      receiving_event_id: input.receivingEventId,
      reason_code: input.reasonCode,
      request_key: input.requestKey,
      note: input.note ?? null,
    });
  }

  private async post<T>(body: Record<string, unknown>): Promise<GatewayEnvelope<T>> {
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await fetch(`${this.baseUrl}/api/pos/runtime/receiving`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
        credentials: "omit",
        signal: controller.signal,
      });
      const payload = (await response.json()) as GatewayEnvelope<T>;
      if (!response.ok) return { ok: false, error: payload.error ?? `GATEWAY_HTTP_${response.status}` };
      return payload;
    } catch (error) {
      return {
        ok: false,
        error: error instanceof DOMException && error.name === "AbortError" ? "GATEWAY_TIMEOUT" : "GATEWAY_UNREACHABLE",
      };
    } finally {
      window.clearTimeout(timeout);
    }
  }
}
